import { useId } from 'react';
import PropTypes from 'prop-types';

import { getCourseBackground } from './catalog';
import './courseBackgrounds.css';

/**
 * The subject art on its own — the tiled motif and the right-pinned hero glyph
 * from the catalogue, with none of `CourseBackground`'s accent gradient or
 * scrim.
 *
 * It exists for the timetable cell (W5.5), where the cell already carries its
 * subject as a 12% tint + edge + ink text (the measurable W-30 plate) and the
 * art is only a watermark that makes an occupied cell findable at a glance. The
 * art is drawn in `currentColor`, so the host sets it to the subject edge and
 * dims it with opacity; the label never sits on raw art.
 *
 * A course with no chosen background renders nothing — the cell is then exactly
 * its tinted self, and the caller need not branch.
 */
const CourseMotif = ({ themeKey, className }) => {
  const theme = getCourseBackground(themeKey);
  // useId gives each instance its own <pattern> id; a shared id is invalid
  // markup and breaks the fill lookup in Safari.
  const patternId = `course-motif-${useId().replace(/:/g, '')}`;

  // The neutral default has no hero and only a token tile — not worth a layer.
  if (!theme.key) return null;

  return (
    <span className={`course-motif${className ? ` ${className}` : ''}`} aria-hidden="true">
      <svg className="course-motif__tile" focusable="false">
        <defs>
          <pattern id={patternId} width="48" height="48" patternUnits="userSpaceOnUse">
            {theme.tile}
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>

      {theme.hero && (
        <svg
          className="course-motif__hero"
          viewBox="0 0 120 100"
          preserveAspectRatio="xMaxYMid meet"
          focusable="false"
        >
          {theme.hero}
        </svg>
      )}
    </span>
  );
};

CourseMotif.propTypes = {
  /** Catalogue key; anything unknown or null renders nothing. */
  themeKey: PropTypes.string,
  className: PropTypes.string,
};

CourseMotif.defaultProps = {
  themeKey: null,
  className: '',
};

export default CourseMotif;
